import type { Point, CanvasCoordinates, ScreenCoordinates } from '@/types';

/**
 * Convert screen coordinates to canvas coordinates
 */
export function screenToCanvas(
  screenX: number,
  screenY: number,
  viewport: { zoom: number; canvasOffset: { x: number; y: number } }
): CanvasCoordinates {
  const canvasX = (screenX - viewport.canvasOffset.x) / viewport.zoom;
  const canvasY = (screenY - viewport.canvasOffset.y) / viewport.zoom;

  return { canvasX, canvasY };
}

/**
 * Convert canvas coordinates to screen coordinates
 */
export function canvasToScreen(
  canvasX: number,
  canvasY: number,
  viewport: { zoom: number; canvasOffset: { x: number; y: number } }
): ScreenCoordinates {
  const screenX = canvasX * viewport.zoom + viewport.canvasOffset.x;
  const screenY = canvasY * viewport.zoom + viewport.canvasOffset.y;

  return { screenX, screenY };
}

/**
 * Get mouse coordinates relative to an element
 */
export function getRelativeMousePosition(
  event: MouseEvent,
  element: HTMLElement | SVGSVGElement
): Point {
  const rect = element.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
}

/**
 * Convert CanvasCoordinates to Point
 */
export function canvasCoordinatesToPoint(coords: CanvasCoordinates): Point {
  return {
    x: coords.canvasX,
    y: coords.canvasY,
  };
}

/**
 * Calculate distance between two points
 */
export function distance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate the angle between two points in radians
 */
export function angle(p1: Point, p2: Point): number {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

/**
 * Rotate a point around another point
 */
export function rotatePoint(
  point: Point,
  center: Point,
  angleRad: number
): Point {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: cos * dx - sin * dy + center.x,
    y: sin * dx + cos * dy + center.y,
  };
}

/**
 * Snap point to grid
 */
export function snapToGrid(point: Point, gridSize: number): Point {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
